import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * The shell every "nothing here" screen shares: a card, the icon in a tinted circle, a heading, and the
 * caller's lines under it. `EmptyState` (a screen not built yet) and `FeatureOffState` (a feature the
 * agency has not turned on) both sit on it, so the two read as one family. The tint is the brand wash: the
 * icon decorates, it never carries meaning. `compact` is the size for a tab or a card inside a sheet.
 * Any other prop (role, aria-labelledby) lands on the card.
 */
export interface StateShellProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon: LucideIcon;
  title: React.ReactNode;
  headingLevel?: 1 | 2 | 3;
  headingId?: string;
  compact?: boolean;
}

export function StateShell({
  icon: Icon,
  title,
  headingLevel = 1,
  headingId,
  compact = false,
  className,
  children,
  ...props
}: StateShellProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <Card className={cn('mx-auto max-w-2xl', className)} {...props}>
      <CardContent className={cn('flex flex-col items-center text-center', compact ? 'gap-3 p-6 sm:p-8' : 'gap-4 p-12')}>
        <span className={cn('grid place-items-center rounded-full bg-accent-wash', compact ? 'size-10' : 'size-12')}>
          <Icon className={cn('text-accent-base', compact ? 'size-5' : 'size-6')} aria-hidden="true" />
        </span>
        <Heading id={headingId} className={cn('font-semibold text-ink', compact ? 'text-lg' : 'text-2xl')}>
          {title}
        </Heading>
        {children}
      </CardContent>
    </Card>
  );
}
